import type { ReactNode } from "react";
import { BarChart3, Wallet2, Trophy, Target, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { SectionTag } from "./SectionTag";

const months = ["Mar", "Mai", "Juil", "Sep", "Nov", "Jan"];
const brut = [958, 2600, 976, 848, 2050, 1050];
const fidelite = [888, 2500, 896, 788, 2000, 1000];
const max = Math.max(...brut);

const topMembers = [
  { name: "Jordan Gastelais", value: "1 326 €" },
  { name: "Mario Petit", value: "864 €" },
  { name: "Tony Riffet", value: "827 €" },
  { name: "Mohamed Mohamed", value: "798 €" },
  { name: "Isaï Siegler", value: "697 €" },
];

interface Row {
  icon: typeof BarChart3;
  accent: string;
  tag: string;
  title: string;
  description: string;
  bullets: string[];
  visual: ReactNode;
}

function OverviewVisual() {
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_220px]">
      <div>
        <p className="text-sm text-ink-soft">25 Mars 2025 – 24 Mars 2026</p>
        <div className="mt-6 flex items-end gap-4" style={{ height: 220 }}>
          {months.map((m, i) => (
            <div key={m} className="flex flex-1 flex-col items-center gap-1.5">
              <div className="flex w-full items-end gap-1" style={{ height: 190 }}>
                <div className="flex-1 rounded-t-md bg-ink/80" style={{ height: `${(brut[i] / max) * 100}%` }} />
                <div className="flex-1 rounded-t-md bg-gradient-brand" style={{ height: `${(fidelite[i] / max) * 100}%` }} />
              </div>
              <span className="text-[11px] text-ink-soft">{m}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4">
        {[
          { label: "Chiffre d'affaires brut", value: "17 298,14 €" },
          { label: "Revenus de fidélité", value: "16 328,14 €" },
        ].map((kpi) => (
          <div key={kpi.label} className="rounded-2xl bg-cream-deep p-4">
            <p className="text-xs text-ink-soft">{kpi.label}</p>
            <p className="mt-1 text-xl font-extrabold text-ink">{kpi.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FinanceVisual() {
  return (
    <div>
      <p className="text-sm font-semibold text-ink">Chiffre d'affaires total</p>
      <p className="text-3xl font-extrabold text-ink">17 298,14 €</p>
      <div className="mt-4 h-3 w-full overflow-hidden rounded-full bg-cream-deep">
        <div className="flex h-full w-full">
          <div className="h-full bg-ink" style={{ width: "85%" }} />
          <div className="h-full bg-gradient-brand" style={{ width: "15%" }} />
        </div>
      </div>
      <ul className="mt-5 space-y-3 text-sm">
        <li className="flex justify-between text-ink-soft">
          <span>Membres réguliers</span>
          <span className="font-semibold text-ink">14 740,13 €</span>
        </li>
        <li className="flex justify-between text-ink-soft">
          <span>Nouveaux membres</span>
          <span className="font-semibold text-ink">2 558,01 €</span>
        </li>
      </ul>
    </div>
  );
}

function TopMembersVisual() {
  return (
    <div>
      <p className="text-sm font-semibold text-ink">Top 5 des membres</p>
      <ul className="mt-4 divide-y divide-line">
        {topMembers.map((m, i) => (
          <li key={m.name} className="flex items-center gap-4 py-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-brand text-xs font-bold text-white">
              {i + 1}
            </span>
            <span className="flex-1 font-medium text-ink">{m.name}</span>
            <span className="font-bold text-ink">{m.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RoiVisual() {
  return (
    <div className="grid gap-6 sm:grid-cols-2">
      <div className="rounded-2xl bg-cream-deep p-5">
        <p className="text-sm font-semibold text-ink">Investissement total</p>
        <p className="mt-1 text-3xl font-extrabold text-ink">2 201,30 €</p>
      </div>
      <div className="rounded-2xl bg-cream-deep p-5">
        <p className="text-sm font-semibold text-ink">Retour sur investissement</p>
        <p className="mt-1 text-3xl font-extrabold text-mint-text">15 096,84 €</p>
      </div>
      <div className="rounded-2xl bg-ink p-6 sm:col-span-2">
        <p className="text-lg font-bold text-white">Chaque euro investi vous en rapporte 7</p>
        <p className="mt-1 text-sm text-white/60">
          Mesurez précisément le retour sur investissement de votre programme de fidélité.
        </p>
      </div>
    </div>
  );
}

const rows: Row[] = [
  {
    icon: BarChart3,
    accent: "bg-coral/10 text-coral",
    tag: "Vue d'ensemble",
    title: "Tout votre chiffre d'affaires, une seule vue.",
    description:
      "Comparez d'un coup d'œil ce que la fidélité rapporte vraiment, mois après mois — sans tableur à mettre à jour.",
    bullets: [
      "Chiffre d'affaires brut vs revenus liés à la fidélité",
      "Évolution sur 12 mois glissants",
      "Filtrez par période en un clic",
    ],
    visual: <OverviewVisual />,
  },
  {
    icon: Wallet2,
    accent: "bg-orange/10 text-orange",
    tag: "Finance",
    title: "Comprenez d'où vient chaque euro.",
    description: "Décomposez votre CA par source de clients et doublez la mise là où ça rapporte vraiment.",
    bullets: [
      "Répartition membres réguliers / nouveaux",
      "Suivi en temps réel, pas de rapport à générer",
      "Comparez d'un mois sur l'autre",
    ],
    visual: <FinanceVisual />,
  },
  {
    icon: Trophy,
    accent: "bg-mint/10 text-mint-text",
    tag: "Top membres",
    title: "Repérez vos meilleurs clients avant vos concurrents.",
    description: "Un classement clair de qui dépense le plus chez vous — pour les chouchouter en priorité.",
    bullets: [
      "Classement automatique par dépenses cumulées",
      "Ciblez-les directement pour les remercier en priorité",
      "Détectez les décrocheurs à forte valeur",
    ],
    visual: <TopMembersVisual />,
  },
  {
    icon: Target,
    accent: "bg-coral/10 text-coral",
    tag: "ROI",
    title: "La preuve chiffrée que ça rapporte.",
    description: "Le retour sur investissement de votre programme de fidélité, calculé pour vous — pas une estimation.",
    bullets: [
      "Investissement total vs revenus générés",
      "Multiple de retour mis à jour en continu",
      "De quoi trancher sans hésiter au renouvellement",
    ],
    visual: <RoiVisual />,
  },
];

export function DashboardPreview() {
  return (
    <section className="bg-cream-deep py-24">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <SectionTag>Performance</SectionTag>
          <h2 className="mt-5 text-4xl font-extrabold tracking-tight text-ink lg:text-5xl">
            Connaissez vos clients pour de vrai
          </h2>
          <p className="mt-4 text-lg text-ink-soft">
            Qui revient, qui décroche, combien ils dépensent — et qui menace de vous
            laisser un avis négatif.
          </p>
          <span className="mt-5 inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-ink-soft shadow-card">
            Exemple illustratif — pas des données réelles
          </span>
        </Reveal>

        <div className="mt-16 flex flex-col gap-20">
          {rows.map((row, i) => (
            <div key={row.tag} className="grid items-center gap-12 lg:grid-cols-2">
              <Reveal delay={0.1} className={i % 2 === 1 ? "lg:order-2" : ""}>
                <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${row.accent}`}>
                  <row.icon size={14} />
                  {row.tag}
                </span>
                <h3 className="mt-4 text-2xl font-extrabold tracking-tight text-ink lg:text-3xl">{row.title}</h3>
                <p className="mt-3 text-ink-soft">{row.description}</p>
                <ul className="mt-5 flex flex-col gap-3">
                  {row.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-3 text-sm text-ink-soft">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-brand" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal delay={0.15} className={i % 2 === 1 ? "lg:order-1" : ""}>
                <div className="rounded-2xl border border-line bg-white p-8 shadow-card">{row.visual}</div>
              </Reveal>
            </div>
          ))}
        </div>

        <Reveal delay={0.1} className="mt-16 flex justify-center">
          <a
            href="#demo"
            className="inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-coral"
          >
            Voir le tableau de bord en démo <ArrowUpRight size={16} />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
